import { useState, useEffect, useMemo } from "react";
import { api } from "../api";
import {
  Loader2, Target, Stethoscope, Building2, UserCircle, TrendingUp,
  TrendingDown, AlertTriangle, Filter, Search, X, ChevronDown, ChevronUp,
  Calendar, Clock, Activity, Printer
} from "lucide-react";

interface DoctorAction {
  specialty: string;
  unit: string;
  doctor: string;
  consultas: number | null;
  consultas_est_2026: number | null;
  meta_consultas_2027: number | null;
  gap_consultas_2027: number | null;
  cirurgias: number | null;
  ocupacao: number | null;
  hours_per_week: number | null;
  diagnosis: string | null;
  action_agreed: string | null;
  responsible: string | null;
  occupancy_rule: string | null;
  low_hours_rule: string | null;
  impact_score: number;
  target_pct: number;
  action_type: string;
  lever: string;
  schedule: {
    total_h_semana: number | null;
    dias: number | null;
    blocos: number | null;
    weekdays: string | null;
    avg_occupancy: number | null;
  } | null;
}

interface UnitAction {
  specialty: string;
  unit: string;
  target_pct: number;
  doctor_count: number;
  consultas: number;
  consultas_est_2026: number;
  meta_consultas_2027: number;
  gap_consultas_2027: number;
  cirurgias: number;
  cirurgias_est_2026: number;
  meta_cirurgias_2027: number;
  gap_cirurgias_2027: number;
  doctors_high_occ: number;
  doctors_low_occ: number;
  doctors_low_hours: number;
  actions: DoctorAction[];
  impact_score: number;
}

interface GrowthData {
  strategy_upload_id: number;
  schedule_upload_id: number | null;
  summary: {
    total_units: number;
    total_doctors: number;
    consultas_est_2026: number;
    meta_consultas_2027: number;
    gap_consultas_2027: number;
    avg_target_pct: number | null;
  };
  units: UnitAction[];
}

function fmtNum(n: number | null | undefined, digits = 0) {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString("pt-PT", { maximumFractionDigits: digits });
}

function fmtPct(n: number | null | undefined) {
  if (n === null || n === undefined) return "—";
  return `${(n * 100).toFixed(1)}%`;
}

function actionTypeColor(t: string) {
  if (t.includes("reforçar")) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (t.includes("captar + otimizar")) return "bg-amber-50 text-amber-700 border-amber-200";
  if (t.includes("captar")) return "bg-blue-50 text-blue-700 border-blue-200";
  return "bg-slate-50 text-slate-700 border-slate-200";
}

export default function GrowthActions({ embedded }: { embedded?: boolean }) {
  const [data, setData] = useState<GrowthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [specialty, setSpecialty] = useState("");
  const [unit, setUnit] = useState("");
  const [search, setSearch] = useState("");
  const [expandedUnits, setExpandedUnits] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<"impact" | "gap" | "doctors">("impact");
  const [actionFilter, setActionFilter] = useState<"all" | "captar" | "reforçar" | "otimizar">("all");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.getGrowthActions(specialty || undefined, unit || undefined)
      .then(setData)
      .catch((err) => setError(err.message || "Failed to load growth actions"))
      .finally(() => setLoading(false));
  }, [specialty, unit]);

  const handleExport = async (autoPrint = false) => {
    try {
      setExporting(true);
      const blob = await api.exportGrowthActions(
        specialty || undefined,
        unit || undefined,
        autoPrint ? "print" : "html"
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.download = `growth-actions-2027-${new Date().toISOString().slice(0, 10)}.html`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const specialties = useMemo(() => {
    if (!data) return [];
    const specs = Array.from(new Set(data.units.map((u) => u.specialty)));
    return specs.sort();
  }, [data]);

  const units = useMemo(() => {
    if (!data) return [];
    const us = Array.from(new Set(data.units.map((u) => u.unit)));
    return us.sort();
  }, [data]);

  const filteredUnits = useMemo(() => {
    if (!data) return [];
    let out = data.units;
    if (search.trim()) {
      const s = search.toLowerCase();
      out = out.filter(
        (u) =>
          u.unit.toLowerCase().includes(s) ||
          u.specialty.toLowerCase().includes(s) ||
          u.actions.some((a) =>
            (a.doctor || "").toLowerCase().includes(s) ||
            (a.lever || "").toLowerCase().includes(s)
          )
      );
    }
    if (actionFilter !== "all") {
      out = out.filter((u) =>
        u.actions.some((a) => {
          if (actionFilter === "captar") return a.action_type.includes("captar");
          if (actionFilter === "reforçar") return a.action_type.includes("reforçar");
          if (actionFilter === "otimizar") return a.action_type.includes("otimizar");
          return true;
        })
      );
    }
    out = [...out].sort((a, b) => {
      if (sortBy === "impact") return b.impact_score - a.impact_score;
      if (sortBy === "gap") return b.gap_consultas_2027 - a.gap_consultas_2027;
      if (sortBy === "doctors") return b.doctor_count - a.doctor_count;
      return 0;
    });
    return out;
  }, [data, search, actionFilter, sortBy]);

  const toggleUnit = (key: string) => {
    const next = new Set(expandedUnits);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setExpandedUnits(next);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin" />
        Loading growth actions...
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-slate-900">Growth Actions 2027</h1>
        <p className="text-slate-500 mt-2">
          {error || "No growth action data available. Upload a strategy workbook with Cálculos 2027."}
        </p>
      </div>
    );
  }

  const body = (
    <>
      {!embedded && (
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Growth Actions 2027</h1>
          <p className="text-slate-500 text-sm mt-1">
            Propostas de crescimento por unidade/especialidade para atingir +20% (não cirúrgicas) / +30% (cirúrgicas).
          </p>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-slate-500 text-xs mb-1">Unidades visíveis</div>
          <div className="text-2xl font-bold text-slate-900">{data.summary.total_units}</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-slate-500 text-xs mb-1">Médicos analisados</div>
          <div className="text-2xl font-bold text-slate-900">{data.summary.total_doctors}</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-slate-500 text-xs mb-1 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> Meta 2027
          </div>
          <div className="text-2xl font-bold text-trofa-700">
            {fmtNum(data.summary.meta_consultas_2027)}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-slate-500 text-xs mb-1 flex items-center gap-1">
            <TrendingDown className="w-3 h-3" /> Gap consultas
          </div>
          <div className={`text-2xl font-bold ${data.summary.gap_consultas_2027 >= 0 ? "text-emerald-700" : "text-red-700"}`}>
            {fmtNum(data.summary.gap_consultas_2027)}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 flex items-center gap-1">
              <Stethoscope className="w-3 h-3" /> Specialty
            </label>
            <select
              value={specialty}
              onChange={(e) => setSpecialty(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-trofa-700 min-w-[160px]"
            >
              <option value="">All</option>
              {specialties.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 flex items-center gap-1">
              <Building2 className="w-3 h-3" /> Unit
            </label>
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-trofa-700 min-w-[160px]"
            >
              <option value="">All</option>
              {units.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 flex items-center gap-1">
              <Search className="w-3 h-3" /> Search
            </label>
            <div className="relative">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="unit, doctor, action..."
                className="border border-slate-300 rounded-lg pl-3 pr-8 py-2 text-sm focus:ring-2 focus:ring-trofa-700 w-[220px]"
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2 top-1.5 text-slate-400 hover:text-slate-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 flex items-center gap-1">
              <Filter className="w-3 h-3" /> Action type
            </label>
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value as any)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-trofa-700"
            >
              <option value="all">All</option>
              <option value="captar">Captar procura</option>
              <option value="reforçar">Reforçar oferta</option>
              <option value="otimizar">Otimizar + captar</option>
            </select>
          </div>
          <div className="flex flex-col gap-1 ml-auto">
            <label className="text-xs text-slate-500 flex items-center gap-1">
              <Target className="w-3 h-3" /> Sort by
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-trofa-700"
            >
              <option value="impact">Impact score</option>
              <option value="gap">Consultation gap</option>
              <option value="doctors"># doctors</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 flex items-center gap-1">
              <Printer className="w-3 h-3" /> Export
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => handleExport(false)}
                disabled={exporting}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                title="Download HTML brief"
              >
                HTML
              </button>
              <button
                onClick={() => handleExport(true)}
                disabled={exporting}
                className="bg-trofa-700 text-white rounded-lg px-3 py-2 text-sm hover:bg-trofa-800 disabled:opacity-50 flex items-center gap-1"
                title="Open printable version"
              >
                <Printer className="w-3.5 h-3.5" /> Print
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Unit cards */}
      <div className="space-y-4">
        {filteredUnits.map((u) => {
          const key = `${u.specialty}|${u.unit}`;
          const isExpanded = expandedUnits.has(key);
          const progressPct = u.meta_consultas_2027
            ? Math.min(100, Math.max(0, ((u.consultas_est_2026 || 0) / u.meta_consultas_2027) * 100))
            : 0;
          return (
            <div key={key} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <button
                onClick={() => toggleUnit(key)}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex flex-col items-start">
                    <span className="font-semibold text-slate-900">{u.unit}</span>
                    <span className="text-xs text-slate-500">{u.specialty} · target +{u.target_pct}%</span>
                  </div>
                  <span className="text-sm text-slate-600">
                    {u.doctor_count} médicos
                  </span>
                  <span className="text-sm font-medium text-slate-700">
                    Gap: {fmtNum(u.gap_consultas_2027)}
                  </span>
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                    Impact {fmtNum(u.impact_score)}
                  </span>
                  {u.doctors_high_occ > 0 && (
                    <span className="text-xs bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full">
                      {u.doctors_high_occ} ≥75%
                    </span>
                  )}
                  {u.doctors_low_occ > 0 && (
                    <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full">
                      {u.doctors_low_occ} &lt;50%
                    </span>
                  )}
                  {u.doctors_low_hours > 0 && (
                    <span className="text-xs bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full">
                      {u.doctors_low_hours} &lt;10h
                    </span>
                  )}
                </div>
                {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
              </button>

              {isExpanded && (
                <div className="px-5 pb-5">
                  <div className="mb-4">
                    <div className="flex justify-between text-xs text-slate-500 mb-1">
                      <span>Progresso vs meta 2027</span>
                      <span>{fmtNum(u.consultas_est_2026)} / {fmtNum(u.meta_consultas_2027)}</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2">
                      <div
                        className="bg-trofa-600 h-2 rounded-full transition-all"
                        style={{ width: `${progressPct}%` }}
                      />
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">Doctor</th>
                          <th className="text-right px-3 py-2 font-medium">Est 2026</th>
                          <th className="text-right px-3 py-2 font-medium">Meta 2027</th>
                          <th className="text-right px-3 py-2 font-medium">Gap</th>
                          <th className="text-right px-3 py-2 font-medium">Ocupação</th>
                          <th className="text-right px-3 py-2 font-medium">h/sem</th>
                          <th className="text-left px-3 py-2 font-medium">Action type</th>
                          <th className="text-left px-3 py-2 font-medium">Proposed lever</th>
                        </tr>
                      </thead>
                      <tbody>
                        {u.actions.map((a, idx) => (
                          <tr key={idx} className="border-t border-slate-100">
                            <td className="px-3 py-2 font-medium text-slate-800">{a.doctor}</td>
                            <td className="px-3 py-2 text-right text-slate-600">{fmtNum(a.consultas_est_2026)}</td>
                            <td className="px-3 py-2 text-right text-slate-600">{fmtNum(a.meta_consultas_2027)}</td>
                            <td className="px-3 py-2 text-right font-medium text-slate-800">{fmtNum(a.gap_consultas_2027)}</td>
                            <td className="px-3 py-2 text-right">
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${
                                (a.ocupacao || 0) >= 0.75
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : (a.ocupacao || 0) >= 0.5
                                  ? "bg-amber-50 text-amber-700 border-amber-200"
                                  : "bg-red-50 text-red-700 border-red-200"
                              }`}>
                                {fmtPct(a.ocupacao)}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right">
                              <span className={(a.hours_per_week || 999) < 10 ? "text-amber-700 font-semibold" : "text-slate-700"}>
                                {a.hours_per_week !== null && a.hours_per_week !== undefined ? `${a.hours_per_week.toFixed(1)}h` : "—"}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${actionTypeColor(a.action_type)}`}>
                                {a.action_type}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-slate-600 max-w-md" title={a.lever}>{a.lever}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );

  if (embedded) return body;
  return <div className="p-6 max-w-7xl mx-auto">{body}</div>;
}
